import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, X, Loader2 } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
import { getTranslatedCategoryLabel } from '../../utils/categoryTranslations';
import { fullButtons } from '../../styles/colors';
import Tooltip from './Tooltip';
import { DROPDOWN_OPTION_IDLE, DROPDOWN_OPTION_SELECTED } from './dropdownMenuStyles';

interface SelectOption {
  value: string;
  label: string;
  category?: {
    id: number;
    user_id: string | null;
  };
}

interface SelectWithAddProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  error?: string;
  onAddOption?: (label: string) => Promise<void>;
  onDeleteOption?: (value: string) => Promise<void>;
  addButtonText?: string;
  addPlaceholder?: string;
  className?: string;
  isCustomOption?: (value: string) => boolean;
  /** When true, show "Chargement..." in the button (spinner on the right) and loading state in the dropdown. Keeps button height stable while options are fetched. */
  loading?: boolean;
}

const SelectWithAdd: React.FC<SelectWithAddProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
  onAddOption,
  onDeleteOption,
  addButtonText,
  addPlaceholder,
  className = "",
  isCustomOption,
  loading: optionsLoading = false,
}) => {
  const { t, language } = useI18n();
  const [isAdding, setIsAdding] = useState(false);
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownListRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(option => option.value === value);

  useEffect(() => {
    if (isAdding) {
      if (inputRef.current) inputRef.current.focus();
      // Scroll dropdown to bottom so the add form (input + button) is visible
      requestAnimationFrame(() => {
        const el = dropdownListRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    }
  }, [isAdding]);

  const handleAddClick = () => {
    setIsAdding(true);
    setNewOptionLabel('');
  };

  const handleAddOption = async () => {
    if (!newOptionLabel.trim() || !onAddOption) return;

    setIsLoading(true);
    try {
      await onAddOption(newOptionLabel.trim());
      setIsAdding(false);
      setNewOptionLabel('');
    } catch (error) {
      console.error('Error adding option:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOption();
    }
  };

  const handleDeleteOption = async (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDeleteOption) {
      try {
        await onDeleteOption(optionValue);
        // Parent (e.g. CategorySelectWithAdd) sets the new value (Other or '') when needed; don't override
      } catch (error) {
        console.error('Error deleting option:', error);
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}
      
      <div className="relative" ref={selectRef}>
        <button
          type="button"
          onClick={() => !optionsLoading && setIsOpen(!isOpen)}
          disabled={optionsLoading}
          className={`
            block w-full min-h-[2.5rem] px-3.5 py-2.5 pr-10 border rounded-xl shadow-sm text-base sm:text-sm text-left
            bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
            disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed dark:disabled:bg-gray-800/50
            leading-5 transition-all
            ${error
              ? 'border-red-300 text-red-900'
              : isOpen
                ? 'border-primary-500 ring-2 ring-primary-500'
                : 'hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm'
            }
          `}
        >
          <span className={`block truncate ${optionsLoading || !selectedOption ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
            {optionsLoading ? t('common.loading') : selectedOption ? selectedOption.label : placeholder}
          </span>
        </button>

        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {optionsLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden />
          ) : (
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180 text-primary-500' : 'text-gray-400'}`} />
          )}
        </div>

        {isOpen && (
          <div
            ref={dropdownListRef}
            className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-paper max-h-60 overflow-auto overflow-x-hidden"
          >
            {optionsLoading ? (
              <div className="px-4 py-3 min-h-24 text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-gray-300 dark:border-gray-600 border-t-primary-500 rounded-full animate-spin" />
                {t('common.loading')}
              </div>
            ) : (
              <>
            {placeholder && !options.some((o) => o.label === placeholder) && (
              <div
                className={`px-3 py-2.5 text-sm cursor-pointer transition-colors ${
                  !value
                    ? DROPDOWN_OPTION_SELECTED
                    : 'text-gray-500 dark:text-gray-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-700 dark:hover:text-primary-300'
                }`}
                onClick={() => {
                  onChange('');
                  setIsOpen(false);
                  // Close add section if it was open
                  if (isAdding) {
                    setIsAdding(false);
                    setNewOptionLabel('');
                  }
                }}
              >
                {placeholder}
              </div>
            )}
            
            {/* Base categories (non‑custom) */}
            {options
              .filter(option => !isCustomOption || !isCustomOption(option.value))
              .map((option) => {
                const isSelected = option.value === value;
                return (
                  <div
                    key={option.value}
                    className={`px-3 py-2.5 text-sm cursor-pointer transition-colors flex items-center justify-between ${
                      isSelected ? DROPDOWN_OPTION_SELECTED : DROPDOWN_OPTION_IDLE
                    }`}
                    onClick={() => {
                      onChange(option.value);
                      setIsOpen(false);
                      if (isAdding) {
                        setIsAdding(false);
                        setNewOptionLabel('');
                      }
                    }}
                  >
                    <span className="truncate">
                      {option.category && option.category.user_id === null
                        ? getTranslatedCategoryLabel({ ...option.category, label: option.label } as any, t, language)
                        : option.label}
                    </span>
                    {isSelected && <Check className="w-4 h-4 text-primary-600 flex-shrink-0 ml-2" />}
                  </div>
                );
              })}
            
            {/* Separator when there are custom categories */}
            {options.some(option => isCustomOption && isCustomOption(option.value)) && (
              <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
            )}
            
            {/* Custom categories */}
            {options
              .filter(option => isCustomOption && isCustomOption(option.value))
              .map((option) => {
                const isSelected = option.value === value;
                return (
                  <div
                    key={option.value}
                    className={`px-3 py-2.5 text-sm cursor-pointer transition-colors flex items-center justify-between group ${
                      isSelected ? DROPDOWN_OPTION_SELECTED : DROPDOWN_OPTION_IDLE
                    }`}
                    onClick={() => {
                      onChange(option.value);
                      setIsOpen(false);
                      if (isAdding) {
                        setIsAdding(false);
                        setNewOptionLabel('');
                      }
                    }}
                  >
                    <span className="truncate">
                      {option.category && option.category.user_id === null
                        ? getTranslatedCategoryLabel({ ...option.category, label: option.label } as any, t, language)
                        : option.label}
                    </span>
                    <span className="flex items-center gap-2">
                      {isSelected && <Check className="w-4 h-4 text-primary-600 flex-shrink-0" />}
                      {onDeleteOption && (
                        <Tooltip content={t('leads.delete')}>
                          <button
                            type="button"
                            onClick={(e) => handleDeleteOption(option.value, e)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-600 transition-opacity"
                            aria-label={t('leads.delete')}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Tooltip>
                      )}
                    </span>
                  </div>
                );
              })}
            
            {onAddOption && (
              <div className="border-t border-gray-200 dark:border-gray-600">
                {!isAdding ? (
                  <div
                    className="px-3 py-2.5 text-sm text-primary-600 dark:text-primary-400 font-medium hover:bg-primary-50 dark:hover:bg-primary-900/20 cursor-pointer transition-colors"
                    onClick={handleAddClick}
                  >
                    + {addButtonText || t('leads.addCategories')}
                  </div>
                ) : (
                  <div
                    className="p-3 flex items-center gap-2 w-full min-w-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      ref={inputRef}
                      type="text"
                      value={newOptionLabel}
                      onChange={(e) => setNewOptionLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setIsAdding(false);
                          setNewOptionLabel('');
                          return;
                        }
                        handleKeyPress(e);
                      }}
                      placeholder={addPlaceholder}
                      className="flex-1 min-w-0 px-3 py-2 text-base sm:text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={handleAddOption}
                      disabled={!newOptionLabel.trim() || isLoading}
                      className={`flex-shrink-0 px-3 py-2 text-sm ${fullButtons.primary} disabled:opacity-50 disabled:cursor-not-allowed rounded-lg`}
                    >
                      {isLoading ? '...' : addButtonText || t('common.add')}
                    </button>
                  </div>
                )}
              </div>
            )}
              </>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};

export default SelectWithAdd;